/**
 * useSnippets Hook
 *
 * CRUD for email snippets + snippet matching logic via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailSnippet, EmailCreateSnippetInput, EmailUpdateSnippetInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

export function useSnippets(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.email.snippets(accountId ?? ''),
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      return api.listEmailSnippets(accountId);
    },
    enabled: !!accountId,
  });

  const snippets = queryResult.data?.snippets ?? ([] as EmailSnippet[]);

  const invalidate = useCallback(() => {
    if (accountId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.email.snippets(accountId) });
    }
  }, [accountId, queryClient]);

  const createSnippet = useCallback(
    async (input: EmailCreateSnippetInput) => {
      const result = await api.createEmailSnippet(input);
      invalidate();
      return result;
    },
    [invalidate],
  );

  const updateSnippet = useCallback(
    async (snippetId: string, input: EmailUpdateSnippetInput) => {
      if (!accountId) return;
      const result = await api.updateEmailSnippet(snippetId, accountId, input);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const deleteSnippet = useCallback(
    async (snippetId: string) => {
      if (!accountId) return;
      await api.deleteEmailSnippet(snippetId, accountId);
      invalidate();
    },
    [accountId, invalidate],
  );

  /** Find a snippet matching the given trigger text (e.g., "/thanks") */
  const findMatch = useCallback(
    (trigger: string): EmailSnippet | null => {
      return snippets.find((s) => s.trigger === trigger) ?? null;
    },
    [snippets],
  );

  /** Find snippets whose trigger starts with the given prefix */
  const findMatches = useCallback(
    (prefix: string): EmailSnippet[] => {
      if (!prefix.startsWith('/')) return [];
      return snippets.filter((s) => s.trigger.toLowerCase().startsWith(prefix.toLowerCase()));
    },
    [snippets],
  );

  return {
    snippets,
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
    createSnippet,
    updateSnippet,
    deleteSnippet,
    findMatch,
    findMatches,
  };
}
